import React from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaArrowRight,
} from "react-icons/fa";
import Services14 from "../images/flogo.png";

/* ── Constants ─────────────────────────────────────── */
const CONTACT = {
  address:  "MRSL - 5, Bengal Ambuja HIG Complex, City Center, Durgapur, WB, India - 713216",
  phone:    "+91 7318731222",
  email:    "info@skywisetechnologies.in",
  gmail:    "https://mail.google.com/mail/?view=cm&fs=1&to=info@skywisetechnologies.in",
  whatsapp: "https://wa.me/917318731222",
};

const QUICK_LINKS = [
  { label: "Home",       to: "/"            },
  { label: "About Us",   to: "/about"       },
  { label: "Technology", to: "/technology"  },
  { label: "Services",   to: "/service"     },
  { label: "Contact",    to: "/contact"     },
];

const SOCIAL_LINKS = [
  { icon: <FaFacebookF />,  href: "https://facebook.com/skywisetechnologies",  label: "Facebook"  },
  { icon: <FaTwitter />,    href: "https://twitter.com/skywisetechnologies",    label: "Twitter"   },
  { icon: <FaLinkedinIn />, href: "https://linkedin.com/company/skywisetechnologies", label: "LinkedIn" },
  { icon: <FaInstagram />,  href: "https://instagram.com/skywisetechnologies",  label: "Instagram" },
];

/* ── Footer ─────────────────────────────────────────── */
function Footer() {
  const year = new Date().getFullYear();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root {
          --ft-bg-top:      #0b1a2e;
          --ft-bg-bottom:   #071220;
          --ft-accent:      #00c2ff;
          --ft-gold:        #f0b429;
          --ft-text:        rgba(255,255,255,0.72);
          --ft-text-bright: #ffffff;
          --ft-border:      rgba(255,255,255,0.08);
          --ft-hover:       rgba(0,194,255,0.12);
          --font-display:   'Syne', sans-serif;
          --font-body:      'DM Sans', sans-serif;
        }

        /* ── Shell ── */
        .ft-footer {
          background: linear-gradient(180deg, var(--ft-bg-top) 0%, var(--ft-bg-bottom) 100%);
          color: var(--ft-text);
          font-family: var(--font-body);
          border-top: 1px solid var(--ft-border);
        }

        /* ── Top strip ── */
        .ft-top-strip {
          border-bottom: 1px solid var(--ft-border);
          padding: 1rem 0;
        }
        .ft-top-inner {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
        }
        .ft-tagline {
          font-size: 0.82rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ft-accent);
          font-weight: 600;
        }
        .ft-social-row {
          display: flex;
          gap: 0.5rem;
        }
        .ft-social-btn {
          width: 32px; height: 32px;
          border-radius: 6px;
          border: 1px solid var(--ft-border);
          background: rgba(255,255,255,0.04);
          color: var(--ft-text);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          text-decoration: none;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .ft-social-btn:hover {
          background: var(--ft-hover);
          color: var(--ft-accent);
          border-color: var(--ft-accent);
        }

        /* ── Main grid ── */
        .ft-main {
          max-width: 1180px;
          margin: 0 auto;
          padding: 3rem 1.5rem 2rem;
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1.6fr;
          gap: 2.5rem;
        }

        /* ── Column headings ── */
        .ft-col-title {
          font-family: var(--font-display);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--ft-gold);
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .ft-col-title::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--ft-border);
        }

        /* ── Brand column ── */
        .ft-logo {
          display: block;
          margin-bottom: 1rem;
        }
        .ft-logo img {
          height: 42px;
          width: auto;
          object-fit: contain;
          filter: brightness(1.1);
        }
        .ft-about-text {
          font-size: 0.88rem;
          line-height: 1.75;
          color: var(--ft-text);
          margin-bottom: 1.25rem;
        }
        .ft-about-text strong { color: var(--ft-text-bright); }

        /* ── Quick links ── */
        .ft-links {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }
        .ft-link {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.4rem 0.5rem;
          border-radius: 6px;
          color: var(--ft-text);
          text-decoration: none;
          font-size: 0.88rem;
          font-weight: 500;
          transition: background 0.2s, color 0.2s, padding-left 0.2s;
        }
        .ft-link svg {
          font-size: 0.6rem;
          color: var(--ft-accent);
          opacity: 0;
          transition: opacity 0.2s;
          flex-shrink: 0;
        }
        .ft-link:hover {
          background: var(--ft-hover);
          color: var(--ft-text-bright);
          padding-left: 0.85rem;
        }
        .ft-link:hover svg { opacity: 1; }

        /* ── Contact items ── */
        .ft-contact-list {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }
        .ft-contact-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          text-decoration: none;
          color: var(--ft-text);
          font-size: 0.87rem;
          line-height: 1.55;
          transition: color 0.2s;
        }
        .ft-contact-item:hover { color: var(--ft-text-bright); }
        .ft-contact-icon {
          width: 30px; height: 30px;
          border-radius: 6px;
          background: rgba(0,194,255,0.1);
          border: 1px solid rgba(0,194,255,0.2);
          color: var(--ft-accent);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          flex-shrink: 0;
          margin-top: 1px;
        }

        /* ── Divider ── */
        .ft-divider {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 1.5rem;
          border: none;
          border-top: 1px solid var(--ft-border);
        }

        /* ── Bottom bar ── */
        .ft-bottom {
          max-width: 1180px;
          margin: 0 auto;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
        }
        .ft-copyright {
          font-size: 0.82rem;
          color: var(--ft-text);
        }
        .ft-copyright strong { color: var(--ft-text-bright); }
        .ft-legal-links {
          display: flex;
          gap: 1.25rem;
        }
        .ft-legal-link {
          font-size: 0.8rem;
          color: var(--ft-text);
          text-decoration: none;
          transition: color 0.2s;
        }
        .ft-legal-link:hover { color: var(--ft-accent); }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .ft-main {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
        }
        @media (max-width: 600px) {
          .ft-main {
            grid-template-columns: 1fr;
            padding: 2rem 1rem 1.5rem;
            gap: 1.75rem;
          }
          .ft-bottom {
            flex-direction: column;
            align-items: flex-start;
            padding: 1rem;
          }
          .ft-top-inner { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <footer className="ft-footer" role="contentinfo">

        {/* ── Top strip: tagline + social ── */}
        <div className="ft-top-strip">
          <div className="ft-top-inner">
            <span className="ft-tagline">
              Empowering businesses through intelligent technology
            </span>
            <div className="ft-social-row" aria-label="Social media links">
              {SOCIAL_LINKS.map(({ icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  className="ft-social-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${label}`}
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* ── Main 4-column grid ── */}
        <div className="ft-main">

          {/* Col 1 — Brand */}
          <div>
            <Link to="/" className="ft-logo" aria-label="Skywise Technologies — Home">
              <img src={Services14} alt="Skywise Technologies logo" />
            </Link>
            <p className="ft-about-text">
              <strong>Skywise Technologies LLP</strong> is a modern software
              company committed to building intelligent, scalable, and secure
              solutions. We empower businesses in mining, logistics, and beyond
              through innovation, data-driven insights, and digital
              transformation.
            </p>
          </div>

          {/* Col 2 — Quick Links */}
          <div>
            <div className="ft-col-title">Quick Links</div>
            <ul className="ft-links">
              {QUICK_LINKS.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} className="ft-link">
                    <FaArrowRight />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Services (static) */}
          <div>
            <div className="ft-col-title">Services</div>
            <ul className="ft-links">
              {[
                { label: "Fleet Management",   to: "#" },
                { label: "Transport System",   to: "#" },
                { label: "IoT Solutions",      to: "#" },
                { label: "AI & Analytics",     to: "#" },
                { label: "IT Consulting",      to: "#" },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="ft-link">
                    <FaArrowRight />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Contact */}
          <div>
            <div className="ft-col-title">Get in Touch</div>
            <div className="ft-contact-list">
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(CONTACT.address)}`}
                className="ft-contact-item"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="ft-contact-icon"><FaMapMarkerAlt /></span>
                <span>{CONTACT.address}</span>
              </a>

              <a
                href={CONTACT.whatsapp}
                className="ft-contact-item"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="ft-contact-icon"><FaPhone /></span>
                <span>{CONTACT.phone}</span>
              </a>

              <a
                href={CONTACT.gmail}
                className="ft-contact-item"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="ft-contact-icon"><FaEnvelope /></span>
                <span>{CONTACT.email}</span>
              </a>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <hr className="ft-divider" />

        {/* ── Bottom bar ── */}
        <div className="ft-bottom">
          <p className="ft-copyright">
            © {year} <strong>Skywise Technologies LLP</strong> — All Rights Reserved.
          </p>
          <div className="ft-legal-links">
            <a href="#" className="ft-legal-link">Privacy Policy</a>
            <a href="#"   className="ft-legal-link">Terms of Use</a>
            <a href="#" className="ft-legal-link">Sitemap</a>
            <a href="#" className="ft-legal-link">DK</a>
          </div>
        </div>

      </footer>
    </>
  );
}

export default Footer;